import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { DataService } from './data.service';
import { concat, forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { FileService } from './file.service';
import { ImportOptions, ParsedImportData, ThirdPartyData, TPSubject } from '../models/import';
import { DateHelper } from 'app/main/content/misc/date.module';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

const httpOptions = {
  headers: new HttpHeaders({ 'timeout': '28800000' }), // MRD - set timeout to approx 8hrs
};
@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class ImportService extends DataService<any> implements OnDestroy {

  resourceUrl = 'api/thirdparty/import';
  subjects: any[];
  uploadedSubjects: any[];

  filesUploaded = 0;

  onProgress: ReplaySubject<any> = new ReplaySubject<any>(1, 100);

  constructor(protected http: HttpClient, protected router: Router, private fileService: FileService, private dateHelper: DateHelper) {
    super(ImportService, 'referralsource', router);
  }

  ngOnDestroy(): void {

  }

  reportProgress(message, value, bufferValue, remainingTime) {
    let time = '';
    if (remainingTime > 0) {
      const hrs = Math.floor(remainingTime / 3600);
      let mins = Math.floor((remainingTime - (hrs * 3600)) / 60);
      let seconds = remainingTime - (((hrs * 3600) + (mins * 60)));
      seconds = Math.ceil(seconds / 15) * 15;
      if (seconds === 60) {
        seconds = 0;
        mins += 1;
      }
      if (hrs > 0) { time += `${hrs} hours `; }
      if (mins > 0) {
        time += `${mins} minute`;
        time += (mins > 1) ? 's ' : ' ';
      }
      if ((hrs + mins) === 0) {
        time += `${seconds} seconds`;
      }
    }
    if (value > 100) {
      value = 100;
    }
    this.onProgress.next({ message, value, bufferValue, remainingTime: time });
  }

  import(json: string, options: ImportOptions): Observable<any> {
    // this.debug('Posting:', json);
    this.reportProgress('Parse Data', 0, 5, 0);
    const data = JSON.parse(json);
    this.uploadedSubjects = [];
    this.filesUploaded = 0;

    data.Subjects = data.Subjects || [];
    data.Users = data.Users || [];
    data.Categories = data.Categories || [];

    // calculate estimated time, in seconds
    // TODO: We currently assume a constant per subject/user.  Change to measure real time and adjust accordingly
    const avgSecondsPerSubject = 0.875;
    const estimatedTimeForUsers = data.Users.length * 8;
    const totalEstimatedTime = estimatedTimeForUsers + Math.round(data.Subjects.length * avgSecondsPerSubject) + 10;

    this.reportProgress('Apply options', 5, 10, totalEstimatedTime);
    if (options && options.subjectNote) {
      // MRD - the date created will be auto set in the backend, therefore we do not need to set it here...
      // const currentDate = this.dateHelper.getDateAsString(undefined, 'DD/MMM/YYYY hh:mm a');
      data.Subjects.forEach((subject) => this.addSubjectNote(subject, options.currentUserName, undefined, options.subjectNote));
    }
    if (options && options.matching) {
      this.applyMatching(data, options);
    }

    const depth = `/${(options && options.directLinkDepth) ? options.directLinkDepth : 2}`;

    // push users on to the stack
    this.reportProgress(`Add Users: 0 of ${data.Users.length}`, 10, 25, totalEstimatedTime);
    const obj = { Subjects: [], Users: [], Categories: [], MarkAsArchived: Boolean };
    obj.Users = data.Users;
    let parsedJson = JSON.stringify(obj);
    return this.http.post<string>(this.resourceUrl + depth, parsedJson, httpOptions)
      .pipe(catchError(this.handleError<string>(`Import`, true)), switchMap((userResult) => {

        if (userResult !== 'Success') {
          // if adding users reported an error then return the error(s)
          const save$ = [];
          save$.push(of(userResult));
          save$.push(of('Abort').pipe(tap((_) => this.reportProgress('Import aborted', 100, 0, 0))));
          return concat(...save$);
        }
        else {
          // no errors reported so far, import the subjects
          const save$ = [];

          // build filemap, if applicable
          const mappedFiles = new Map<string, File>();
          if (options.files) {
            let i = 0;
            while (i < options.files.length) {
              mappedFiles.set(options.files[i].name.toLowerCase(), options.files[i]);
              i++;
            }
          }

          // push subjects on to the stack in chunks
          obj.Users = [];
          obj.MarkAsArchived = data.MarkAsArchived;
          let cnt = 0;
          const chunkSize = 100;
          const percentage = (100 / data.Subjects.length) * 0.7;
          while (cnt < data.Subjects.length) {
            obj.Subjects = data.Subjects.slice(cnt, cnt + chunkSize);
            if (obj.Subjects.length > 0) {
              parsedJson = JSON.stringify(obj);
              const fromCount = cnt;
              const toCount = cnt + obj.Subjects.length;
              save$.push(of('').pipe(tap((_) => {
                const estimatedTime = (totalEstimatedTime - estimatedTimeForUsers) - Math.round(fromCount * avgSecondsPerSubject);
                this.reportProgress(`Add Subjects: ${fromCount} of ${data.Subjects.length}`,
                  Math.round(25 + (fromCount * percentage)), Math.round(25 + (toCount * percentage)),
                  estimatedTime);
              })));
              save$.push(this.http.post<string>(this.resourceUrl + depth, parsedJson, httpOptions).pipe(
                catchError(this.handleError<string>(`Import`, true)),
              ));
              // ...upload any associated files
              if (mappedFiles.size > 0) {
                save$.push(this.saveFiles(obj.Subjects, mappedFiles));
              }
            }
            cnt += chunkSize;
          }

          // push category links on to the stack
          obj.Subjects = [];
          obj.Users = [];
          obj.Categories = data.Categories;
          parsedJson = JSON.stringify(obj);
          save$.push(of('').pipe(tap((_) => this.reportProgress('Add Links', 95, 100, 10))));
          save$.push(this.http.post<string>(this.resourceUrl + depth, parsedJson, httpOptions).pipe(
            catchError(this.handleError<string>(`Import`, true)),
          ));

          // finish/tidy up
          obj.Subjects = [];
          obj.Users = [];
          obj.Categories = [];
          parsedJson = JSON.stringify(obj);
          save$.push(of('').pipe(tap((_) => this.reportProgress('Tidy up', 95, 100, 10))));
          save$.push(this.http.post<string>(this.resourceUrl + depth, parsedJson, httpOptions).pipe(
            catchError(this.handleError<string>(`Import`, true)),
          ));

          // push notification import has completed on to stack
          save$.push(of('Import completed').pipe(tap((_) => this.reportProgress('Import completed', 100, 100, 0))));
          return concat(...save$).pipe(
            tap((result: string) => {
              this.log(`Import reported ${result}`);
            }),
            catchError(this.handleError<string>(`Import`, true)));
        }
      }));
  }

  importFiles(json: string, folders: Map<string, File[]>): Observable<any> {
    this.uploadedSubjects = [];
    this.filesUploaded = 0;
    this.reportProgress('Parsing Data', 0, 0, 0);
    const data: ThirdPartyData = JSON.parse(json);
    const save$ = [];
    if (data && (folders.size > 0)) {
      const parsedData = new ParsedImportData();
      parsedData.subjects = data.Subjects;
      const uniqueSubjects = Array.from(parsedData.getSubjects().values()).filter((s) => s.Files.length > 0);

      let totalFilesToFind = 0;
      uniqueSubjects.forEach((s) => totalFilesToFind += s.Files.length);

      let totalFiles = 0;
      const fileMap = new Map<string, Map<string, File>>();
      folders.forEach((value, key) => {
        totalFiles = totalFiles + value.length;
        const mappedFiles = new Map<string, File>();
        let i = 0;
        while (i < value.length) {
          mappedFiles.set(value[i].name.toLowerCase(), value[i]);
          i++;
        }
        fileMap.set(key, mappedFiles);
      });

      const chunkSize = 5;
      let cnt = 0;
      this.reportProgress(`Uploaded files for 0 of ${uniqueSubjects.length} subjects`, 5, 0, 0);
      while (cnt < uniqueSubjects.length) {
        const subjects = uniqueSubjects.slice(cnt, cnt + chunkSize);
        const processed = cnt;
        const subjectProgress = cnt / uniqueSubjects.length;
        fileMap.forEach((value, key) => {
          save$.push(this.saveFiles(subjects, value).pipe(tap((_) => {
            const progress = Math.round((subjectProgress * (value.size / totalFiles)) * 100);
            this.reportProgress(`Uploaded files for ${processed + chunkSize} of ${uniqueSubjects.length} subjects`, progress + 5, 0, 0);
          })));
        });
        cnt += chunkSize;
      }
    }

    // push notification import has completed on to stack
    save$.push(of('Import completed').pipe(tap((_) => this.reportProgress('Import completed', 100, 100, 0))));
    return concat(...save$).pipe(
      tap((result: string) => {
        this.log(`Import reported ${result}`);
      }),
      catchError(this.handleError<string>(`Import`, true)));
  }

  public saveFiles(subjects: any[], files: Map<string, File>) {
    const upload$ = [];

    if (files && (files.size > 0)) {
      this.subjects = [];
      subjects.forEach((x) => this.getSubjects(x));

      this.subjects.forEach((x) => {
        if (!this.uploadedSubjects.find((i) => i.Uid === x.Uid)) {
          let errors = '';
          const filesToUpload = [];
          if (x.Files.length > 0) {
            x.Files.forEach((d) => {
              const filename: string = d.Filename.toLowerCase();
              const filenameWithoutPath: string = d.Filename.split('\\').pop();
              if (!this.fileService.extensionAllowed(filename)) {
                errors += `\nFile ${d.Filename} for subject ${x.Reference} cannot be uploaded. The file extension is not supported.`;
              }
              else {
                const matchedFile = files.get(filenameWithoutPath);
                if (matchedFile) {
                  if (matchedFile.size > this.fileService.maxFileImportSizeMb * 1024 * 1024) {
                    errors += `\nFound file ${d.Filename} for subject ${x.Reference} but it is too big for upload.  The file has not been imported`;
                  }
                  else {
                    (matchedFile as any).summary = d.Description;
                    filesToUpload.push(matchedFile);
                  }
                }
                else {
                  errors += `\nUnable to find file ${d.Filename} for subject ${x.Reference}.  The file has not been imported`;
                }
              }
            });
            if (filesToUpload.length > 0) {
              upload$.push(this.uploadFiles(x, filesToUpload));
            }
            if (errors) {
              upload$.push(of(errors));
            }
          }
          else {
            // no files but add to our tracker anyway so we don't process again
            this.uploadedSubjects.push(x);
          }
        }
      });
    }

    return upload$.length > 0 ? forkJoin(upload$) : of(null);
  }

  public uploadFiles(subject: any, files: any[]): Observable<any> {
    if ((!subject) || (files.length === 0)) {
      this.uploadedSubjects.push(subject);
      return of(null);
    }

    this.fileService.setParent({ id: subject.ID, uid: subject.Uid, type: 'subject', summary: subject.Summary || subject.Reference });
    this.filesUploaded = this.filesUploaded + files.length;
    return this.fileService.uploadFiles(files).pipe(tap((_) => { this.uploadedSubjects.push(subject); }));
  }

  private getSubjects(subject: any) {
    if (!this.subjects.find((x) => x.Uid === subject.Uid)) {
      this.subjects.push(subject);
      subject.LinkedSubjects.forEach((e) => {
        this.getSubjects(e);
      });
    }
  }

  private addSubjectNote(subject: TPSubject, currentUserName: string, currentDate, note: string) {
    let sequenceId = 1;
    if (subject.Notes.length > 0) {
      // get the max note sequence from current notes
      const max = subject.Notes.reduce((a, b) => (a.y > b.y) ? a : b).Sequence;
      sequenceId = parseInt(max, 10);
      sequenceId = (isNaN(sequenceId)) ? 1 : sequenceId + 1;
    }
    subject.Notes.push({
      Sequence: sequenceId,
      Notes: note,
      AddedBy: currentUserName,
      AddedDate: currentDate,
      Sensistive: false,
    });
  }

  private applyMatching(data, options: ImportOptions) {
    switch (options.matching.toLowerCase()) {
      case 'auto':
        // if we clear the Uid then the import will attempt to match the incoming subject
        // based on subject type and reference.  This isn't guaranteed to work as it is entirely possible
        // that the reference was changed due to a duplicate reference etc, but this is as good as it gets for now.
        // Thus to set the data to attempt to auto match on import we just need to clear the Uid
        // assigned to each subject and each linked subject etc
        data.Subjects.forEach((subject) => this.clearSubjectUid(subject));
        break;
      case 'review':
        break;
      default:
        // if ignore, or not specified, then keep the Uid and import as usual
        // this will in effect import as new subjects as the assigned Uid will be unique
        break;
    }
  }

  private clearSubjectUid(subject) {
    if (subject) {
      if (subject.Uid) {
        subject.Uid = null;
      }
      if (subject.LinkedSubjects) {
        subject.LinkedSubjects.forEach((linkedSubject) => this.clearSubjectUid(linkedSubject));
      }
    }
  }
}